<template>
  <div class="manage-gallery-page">
    <AdminNavbar />
    
    <div class="gallery-management-container">
      
      <h2 class="page-title">Gallery Management ({{ galleryItems.length }} Images)</h2>

      <section class="add-picture-section">
        <h3>➕ Add New Picture</h3>
        <form @submit.prevent="addPicture" class="add-form">
          
          <div class="input-column">
            <div class="form-group">
              <label for="caption">Caption</label>
              <input type="text" id="caption" v-model="newPicture.caption" required placeholder="Describe the service or style">
            </div>
            
            <div class="form-group">
              <label for="category">Category</label>
              <select id="category" v-model="newPicture.category" required>
                <option value="" disabled>Select Category</option>
                <option value="hair">Hair</option>
                <option value="nails">Nails</option>
                <option value="skin">Skin</option>
                <option value="misc">Miscellaneous</option>
              </select>
            </div>

            <div class="form-group action-group">
              <button type="submit" class="btn-add-picture">Add Picture to Gallery</button>
            </div>
          </div>
          
          <div class="upload-column">
            
            <div class="form-group file-group">
              <label for="file-upload">Upload Image</label>
              <input 
                type="file" 
                id="file-upload" 
                @change="handleFileUpload" 
                accept="image/*" 
                required
              />
              <p class="file-status">{{ newPicture.mock_filename || 'No file selected' }}</p>
            </div>
            
            <div class="preview-area">
              <img v-if="newPicture.preview_url" :src="newPicture.preview_url" alt="Image Preview" class="image-preview">
              <p v-else class="preview-placeholder">Image Preview</p>
            </div>
          </div>
        </form>
      </section>
      
        <section class="current-gallery-section">
        <h3>Current Gallery Items</h3>
        
        <div class="gallery-grid">
          <div 
            class="gallery-card" 
            v-for="item in galleryItems" 
            :key="item.id"
          >
            <div class="image-wrapper">
              <div class="mock-image-placeholder">
                  [Image: {{ item.image_path.split('/').pop() }}]
              </div>
            </div>
            
            <div class="card-info">
              <p class="card-caption">**{{ item.caption }}**</p>
              <p class="card-category">Category: {{ item.category }}</p>
              <p class="card-id">ID: {{ item.id }}</p>
            </div>

            <button @click="deletePicture(item.id)" class="btn-delete">Delete</button>
          </div>
        </div>

        <p v-if="galleryItems.length === 0" class="no-items-msg">The gallery is empty. Add a new picture above.</p>
      </section>

      </div>
  </div>
</template>

<script>
import AdminNavbar from '@/components/admin/AdminNavbar.vue'
import initialGalleryData from '@/assets/gallery/gallery_info.json'; 

// --- LOCAL STORAGE UTILITIES ---
const GALLERY_STORAGE_KEY = 'salonGallery';

const saveGalleryToLocalStorage = (data) => {
    localStorage.setItem(GALLERY_STORAGE_KEY, JSON.stringify(data));
    console.log("Gallery data successfully saved to localStorage.");
};

export default {
    name: 'ManageGallery',
    components: {
        AdminNavbar
    },
    data() {
        return {
            galleryItems: this.loadGallery(),
            newPicture: {
                caption: '',
                category: '',
                mock_filename: '',
                preview_url: null
            }
        };
    },
    created() {
        const adminInfo = localStorage.getItem("adminUser")
        let user = JSON.parse(adminInfo)
        if(!adminInfo) {
            this.$router.push({ name: 'Authenticate'})
            console.log("Sign in before you can load the component")
        } else if(!user.access_token) {
            this.$router.push({name: 'Authenticate'})
            console.log("Sign in before you can load the component")
        }
    },
    methods: {
        loadGallery() {
            const storedGallery = localStorage.getItem(GALLERY_STORAGE_KEY);
            if (storedGallery) {
                return JSON.parse(storedGallery);
            }
            // Initialize from JSON and save to storage
            saveGalleryToLocalStorage(initialGalleryData);
            return initialGalleryData;
        },

        /// New
        handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            // 1. Store filename (to simulate the path for the mock API)
            this.newPicture.mock_filename = file.name;

            // 2. Create a FileReader to generate a data URL for client-side preview
            const reader = new FileReader();
            reader.onload = (e) => {
                this.newPicture.preview_url = e.target.result;
            };
            reader.readAsDataURL(file);
        },
        
        addPicture() {
            // 1. Basic Validation (assuming all fields are non-empty due to 'required' attribute)
            if (!this.newPicture.preview_url) {
                alert("Please upload an image")
                return;
            }

            // 2. Create the new item object
            const newItem = {
                id: Math.max(0, ...this.galleryItems.map(i => i.id || 0)) + 1, // Auto-increment ID
                caption: this.newPicture.caption,
                category: this.newPicture.category,
                // Server path when connecting to API
                image_path: `@/assets/images/${this.newPicture.mock_filename}`
            };

            console.log("MOCK API CALL: Saving picture data to backend:", newItem);
            alert(`MOCK API SUCCESS: Picture ${newItem.caption} added.`);

            // 3. Update state and persistence
            this.galleryItems.push(newItem);
            saveGalleryToLocalStorage(this.galleryItems);

            alert(`Picture added: ${newItem.caption}`);

            // 4. Reset form
            this.newPicture = { caption: '', category: '', mock_filename: '' };
        },
        
        deletePicture(id) {
            if (!confirm(`Are you sure you want to delete picture ID ${id}?`)) {
                return;
            }
            
            // Filter out the deleted item
            this.galleryItems = this.galleryItems.filter(item => item.id !== id);
            
            // Update persistence
            saveGalleryToLocalStorage(this.galleryItems);
            alert(`Picture ID ${id} deleted.`);
        }
    }
}
</script>

<style scoped>
.manage-gallery-page {
    min-height: 100vh;
    background-color: #f4f4f9;
}

.gallery-management-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

.page-title {
    /* --primary-teal: #00897B */
    color: #00897B; 
    text-align: center;
    margin-bottom: 30px;
}

section {
    background-color: white;
    padding: 25px;
    margin-bottom: 30px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

section h3 {
    /* --accent-crimson: #DC143C */
    color: #DC143C; 
    border-bottom: 1px solid #eee;
    padding-bottom: 10px;
    margin-bottom: 20px;
}

/* --- Add Picture Form --- */
.add-form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    align-items: start;
    padding: 20px 0;
}

.input-column, .upload-column {
    display: flex;
    flex-direction: column;
    gap: 15px; /* Spacing between form groups */
    /* Use padding to visually align the height */
}

.form-group {
    /* Set a minimum height for visual consistency */
    min-height: 75px; 
    margin-bottom: 0; /* Remove default margin */
}

.form-group label {
    /*display: block; */
    font-weight: 600;
    margin-bottom: 5px;
    color: #444;
}

.form-group input, .form-group select {
    width: 100%;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-sizing: border-box;
}


.file-group {
    /* Hide default file input appearance */
    position: relative;
    overflow: hidden;
}

.file-group input[type="file"] {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 2;
}

.file-status {
    /* Style to make the input area look like a button/input field */
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: #f8f8f8;
    color: #666;
    pointer-events: none; /* Prevents text from blocking file input click */
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.file-group label {
    z-index: 3;
    pointer-events: none;
    margin-bottom: 5px;
}



.form-group small {
    display: block;
    margin-top: 5px;
    color: #888;
    font-size: 0.8rem;
}

.btn-add-picture {
    padding: 12px 20px;
    /* --add-green: #28a745 */
    background-color: #28a745; 
    color: white;
    border: none;
    border-radius: 5px;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.3s;
    grid-column: span 1; 
}

.btn-add-picture:hover {
    background-color: #1e7e34;
}

/* --- Gallery Grid --- */
.gallery-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
}

.gallery-card {
    border: 1px solid #ddd;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    position: relative;
}

.image-wrapper {
    height: 200px;
    width: 100%;
    overflow: hidden;
    border-bottom: 1px solid #eee;
}

.mock-image-placeholder {
    height: 100%;
    background-color: #f0f0f0;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #aaa;
    font-style: italic;
    font-weight: 500;
}

.card-info {
    padding: 15px;
}

.card-caption {
    font-size: 1rem;
    color: #333;
    margin: 0 0 8px 0;
}

.action-group {
    display: flex;
    align-items: flex-end; /* Push button to the bottom of its allocated space */
    min-height: 75px; 
}

.card-category, .card-id {
    font-size: 0.9rem;
    color: #666;
    margin: 0 0 3px 0;
}

.btn-delete {
    /* --accent-crimson: #DC143C */
    background-color: #DC143C;
    color: white;
    padding: 8px;
    border: none;
    cursor: pointer;
    transition: background-color 0.3s;
    font-weight: bold;
    margin-top: auto;
}

.btn-delete:hover {
    background-color: #a00d2b;
}

.no-items-msg {
    text-align: center;
    color: #888;
    grid-column: 1 / -1;
    padding: 20px;
}

.preview-area {
    /* Ensures the preview area maintains a consistent size */
    height: 180px; 
    border: 1px dashed #ccc;
    border-radius: 4px;
    background-color: #fafafa;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
}

.image-preview {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.preview-placeholder {
    color: #aaa;
    font-style: italic;
}

/* Button style remains the same */
.btn-add-picture {
    width: 100%;
    padding: 12px 20px;
    background-color: #28a745;
    color: white;
    border: none;
    border-radius: 5px;
    font-weight: bold;
    cursor: pointer;
}

@media (max-width: 600px) {
    /* Adjust grid for very small screens if 280px is too big */
    .gallery-grid {
        /* Allow smaller minimum card size or stick to 1 column if necessary */
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    }
}
@media (max-width: 768px) {
    /* 1. Stack the Add Picture Form columns */
    .add-form {
        grid-template-columns: 1fr; /* Single column layout */
        gap: 20px;
    }

    /* Remove minimum height constraints on mobile for natural flow */
    .form-group,
    .action-group {
        min-height: auto; 
    }
    
    .action-group {
        /* Add spacing above the full-width button */
        padding-top: 10px; 
    }
    
    /* 2. Stack Gallery Grid to show one card per row */
    .gallery-grid {
        /* Forces the grid to a single, full-width column */
        grid-template-columns: 1fr; 
    }
    
    /* Optional: Increase padding on sections for better spacing on mobile */
    section {
        padding: 20px 15px; 
    }
    
    /* Ensure image wrapper still looks good */
    .image-wrapper {
        height: 250px; /* Slightly taller image area on mobile for better visibility */
    }
}

@media (max-width: 480px) {
    /* Adjustments for smaller phone screens */
    .gallery-management-container {
        padding: 10px;
    }
}
</style>